import React, { useContext, useMemo } from 'react';
import { OFFER_ADDITIONAL_DATA_INVALIDATED } from 'features/offer/edit/financials/additionalData/subscriptions';
import {
  INSURANCE_SIMULATIONS_INVALIDATED,
  LEASING_DOCUMENT_DETAIL_EQUIPMENT_SAVED,
  OFFER_FINANCING_STRUCTURE_INVALIDATED,
  INSURANCE_SIMULATIONS_UPDATED
} from 'features/offer/edit/insurances/subscriptions';
import { OFFER_VAT_FINANCING_INVALIDATED } from 'features/offer/edit/financials/vatFinancing/subscriptions';
import { useApolloClient } from '@apollo/client';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { useSubscription } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { getInt } from 'utils/functions';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { DOCUMENT_UPDATED, DOCUMENTS_COMPUTED } from '../../../features/document/edit/subscriptions';
import { makeReference } from '@apollo/client';
import { useToast } from 'utils/hooks';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { getDocumentType } from 'features/offer/edit/client/form/selectors';
import { getRightToViewInsuranceByDocumentType } from 'constants/common';
import { selectUserId } from 'selectors/userInfoSelectors';
import { RECOMPUTE_SIMULATIONS_COMPLETED } from 'features/offer/edit/productSelection/subscriptions';
import * as productSelectionActionTypes from 'features/offer/edit/productSelection/actionTypes';
import { showInfo } from 'actions/actionCreators/snackbarActions';

const DocumentSubscriptionArea = () => {
  const { documentId, siteId } = useParams();
  const { webInsuranceIntegration } = useContext(ApplicationFeaturesContext);
  const client = useApolloClient();
  const addToast = useToast();
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const userId = useSelector(state => selectUserId(state));
  const documentTypeId = useSelector(state => getDocumentType(state));
  const hasInsuranceRightsByDocumentType = getRightToViewInsuranceByDocumentType(documentTypeId);

  const isNewDocument = useMemo(() => getInt(documentId) === null || getInt(siteId) === null, [documentId, siteId]);

  const invalidateCachedLeasingSimulation = () => {
    const offerCacheId = client.cache.identify({
      __typename: 'LeasingOffer',
      documentId,
      siteId
    });
    client.cache.evict({ id: offerCacheId, fieldName: 'leasingSimulation' });
  };

  useGlobalSubscription(OFFER_ADDITIONAL_DATA_INVALIDATED, {
    skip: isNewDocument,
    variables: { documentId: getInt(documentId), siteId: getInt(siteId) },
    onSubscriptionData: ({
      subscriptionData: {
        data: { offerAdditionalDataInvalidated }
      }
    }) => {
      if (!offerAdditionalDataInvalidated) return;
      invalidateCachedLeasingSimulation();
      const leasingSimulationCacheId = client.cache.identify({
        __typename: 'LeasingSimulation',
        documentId: offerAdditionalDataInvalidated.documentId,
        siteId: offerAdditionalDataInvalidated.siteId
      });
      client.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: `additionalData({"documentId":${documentId},"siteId":${siteId}})`
      });
      client.cache.evict({ id: leasingSimulationCacheId });
      client.cache.gc();
    }
  });

  useGlobalSubscription(OFFER_FINANCING_STRUCTURE_INVALIDATED, {
    skip: isNewDocument,
    variables: { offerId: getInt(documentId), offerSiteId: getInt(siteId) },
    onSubscriptionData: ({
      subscriptionData: {
        data: { offerFinancingStructureInvalidated }
      }
    }) => {
      if (offerFinancingStructureInvalidated) {
        invalidateCachedLeasingSimulation();
        const leasingSimulationCacheId = client.cache.identify({
          __typename: 'LeasingSimulation',
          documentId: offerFinancingStructureInvalidated.documentId,
          siteId: offerFinancingStructureInvalidated.siteId
        });
        client.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: `leasingSimulationByOffer({"offerId":${getInt(documentId)},"offerSiteId":${getInt(siteId)}})`
        });
        client.cache.evict({ id: leasingSimulationCacheId });
        client.cache.gc();
      }
    }
  });

  useGlobalSubscription(LEASING_DOCUMENT_DETAIL_EQUIPMENT_SAVED, {
    skip: isNewDocument,
    variables: { offerId: getInt(documentId), offerSiteId: getInt(siteId) },
    onSubscriptionData: ({
      subscriptionData: {
        data: { leasingDocumentDetailEquipmentSaved }
      }
    }) => {
      if (leasingDocumentDetailEquipmentSaved) {
        const insuranceSimulationCacheId = client.cache.identify({
          __typename: 'InsuranceSimulation',
          id: leasingDocumentDetailEquipmentSaved.insuranceSimulationId
        });

        client.cache.evict({ id: insuranceSimulationCacheId, fieldName: 'assetSafetyPackages' });
      }
    }
  });

  useGlobalSubscription(INSURANCE_SIMULATIONS_UPDATED, {
    skip: isNewDocument,
    variables: { offerId: getInt(documentId), offerSiteId: getInt(siteId) },
    onSubscriptionData: ({
      subscriptionData: {
        data: { insuranceSimulationsUpdated }
      }
    }) => {
      const insuranceSimulationIds = insuranceSimulationsUpdated?.insuranceSimulationIds;
      insuranceSimulationIds.forEach(insuranceSimulationId => {
        const insuranceSimulationCacheId = client.cache.identify({
          __typename: 'InsuranceSimulation',
          id: insuranceSimulationId
        });
        client.cache.evict({ id: insuranceSimulationCacheId });
      });
      client.cache.gc();
    }
  });

  useGlobalSubscription(INSURANCE_SIMULATIONS_INVALIDATED, {
    skip: !webInsuranceIntegration || !hasInsuranceRightsByDocumentType || isNewDocument,
    variables: { offerId: getInt(documentId), offerSiteId: getInt(siteId) },
    onSubscriptionData: ({
      subscriptionData: {
        data: { insuranceSimulationsInvalidated }
      }
    }) => {
      if (insuranceSimulationsInvalidated?.insuranceTypeNames?.length > 0)
        addToast(
          t('Offer.Insurance.RegenerateInsurances', {
            data: { insuranceTypeNames: insuranceSimulationsInvalidated?.insuranceTypeNames }
          }),
          'info'
        );
      invalidateCachedLeasingSimulation();

      const insuranceSimulationIds = insuranceSimulationsInvalidated?.insuranceSimulationIds;
      insuranceSimulationIds.forEach(insuranceSimulationId => {
        const insuranceSimulationCacheId = client.cache.identify({
          __typename: 'InsuranceSimulation',
          id: insuranceSimulationId
        });
        client.cache.evict({ id: insuranceSimulationCacheId });
        client.cache.evict({
          id: client.cache.identify(makeReference('ROOT_QUERY')),
          fieldName: `quotations({"insuranceSimulationId":${insuranceSimulationId}})`
        });
      });
      client.cache.gc();
    }
  });

  useGlobalSubscription(DOCUMENT_UPDATED, {
    skip: isNewDocument,
    variables: { documentId: getInt(documentId), siteId: getInt(siteId), userId },
    onSubscriptionData: data => {
      if (data) dispatch(showInfo(t('Offer.ModifiedData'), false, true));
    }
  });

  useGlobalSubscription(DOCUMENTS_COMPUTED, {
    skip: isNewDocument,
    variables: { documentId: getInt(documentId), siteId: getInt(siteId) },
    onSubscriptionData: data => {
      if (data) dispatch(showInfo(t('Offer.DocumentsComputed'), false, true));
    }
  });

   useSubscription(RECOMPUTE_SIMULATIONS_COMPLETED, {
    skip: isNewDocument,
    variables: { documentId: parseInt(documentId), siteId: parseInt(siteId) },
    onSubscriptionData: ({
      subscriptionData: {
        data: { recomputeSimulationsCompleted }
      }
    }) => {
      if (recomputeSimulationsCompleted) {
        dispatch({
          type: productSelectionActionTypes.SIMULATIONS_RECOMPUTE_SUCCEEDED,
          payload: {
            recomputeSimulationsCompleted
          }
        });
      }
    }
  });

  useGlobalSubscription(OFFER_VAT_FINANCING_INVALIDATED, {
    skip: isNewDocument,
    variables: { documentId: getInt(documentId), siteId: getInt(siteId) },
    onSubscriptionData: data => {
      if (!data) return;
      client.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: `vatFinancingDocument({"documentId":${documentId},"siteId":${siteId}})`
      });

      client.cache.gc();
    }
  });

  return <></>;
};

export default DocumentSubscriptionArea;
